// Layer 3 (Transformation) — the event-log projection (a20-maintenance S1b).
// Pure: no React, no engine mutations, no formatting, no money.
//
// One row per stoppage from YTD_START to the basis close. The rows inside the
// 30-day basis are generated from the merged `DOWNTIME_STATES` intervals, so
// the log and the Downtime timeline can never disagree; the rows before the
// basis are the typed history. S2 (Causes), S3 (Reliability), S4 (Value lost)
// and S5 (PM schedule) all read this log.
//
// durable: event-log projection; identical over a CMMS feed (ADR-0012).

import type { CrewId } from '@/versions/v1/schema/seat';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { diffMinutes } from '@/versions/v1/shared/clock.derive';
import { activeCrewId } from '@/versions/v1/shared/bunker-hill-org-seed';
import {
  BASIS_END,
  CAUSE_CATEGORIES,
  DOWNTIME_ITEMS,
  DOWNTIME_STATES,
  type CauseCategoryId,
  type DowntimeItemId,
  type ItemState,
} from './downtime.seed';
import { HISTORY_EVENTS, ITEM_BUFFER_HOURS, YTD_START, type HistoryEvent } from './downtime-events.seed';
import { mergeAdjacentSameState } from './Downtime.transform';

export type DowntimeEvent = HistoryEvent & {
  /** 'de-0001' … in start order over the full log. */
  id: string;
  /** diffMinutes(end, start) / 60 — computed, never typed. */
  hours: number;
  bufferHours: number;
  /** 'interval' = generated from DOWNTIME_STATES; 'history' = HISTORY_EVENTS. */
  origin: 'interval' | 'history';
};

/** The interval states that count as a stoppage, and the kind each maps to. */
const STOPPAGE_KIND: Readonly<Partial<Record<ItemState['state'], DowntimeEvent['kind']>>> = {
  offline_breakdown: 'unplanned',
  offline_shutdown: 'planned',
};

/** One event per merged stoppage interval for one item. A kept interval that
 *  reaches here uncoded is a seed defect — the maintenance seed already fails
 *  closed on it, and this throws rather than defaulting a category, because a
 *  default would make every Pareto and reliability read below it quietly wrong. */
function eventsFromInterval(itemId: DowntimeItemId, row: ItemState): DowntimeEvent {
  const kind = STOPPAGE_KIND[row.state];
  if (kind === undefined) {
    throw new Error(`DowntimeEvents: ${itemId} ${row.start} ${row.state} is not a stoppage`);
  }
  const { causeCategory, subCause } = row;
  if (causeCategory === undefined || subCause === undefined || subCause.trim().length === 0) {
    throw new Error(`DowntimeEvents: ${itemId} ${row.start} uncoded`);
  }
  // Past the throw both fields are narrowed, so they assign without a default.
  return {
    id: '',
    itemId,
    kind,
    start: row.start,
    end: row.end,
    causeCategory,
    subCause,
    note: row.note,
    workOrderId: row.workOrderId,
    hours: diffMinutes(row.end, row.start) / 60,
    bufferHours: ITEM_BUFFER_HOURS[itemId],
    origin: 'interval',
  };
}

/** The full year-to-date log, built once. `downtimeEvents()` only filters it. */
const FULL_LOG: readonly DowntimeEvent[] = (() => {
  const rows: DowntimeEvent[] = [];
  for (const { id } of DOWNTIME_ITEMS) {
    const merged = mergeAdjacentSameState(DOWNTIME_STATES.filter((row) => row.itemId === id));
    for (const row of merged) {
      if (STOPPAGE_KIND[row.state] === undefined) continue;
      rows.push(eventsFromInterval(id, row));
    }
  }
  for (const event of HISTORY_EVENTS) {
    rows.push({
      ...event,
      id: '',
      hours: diffMinutes(event.end, event.start) / 60,
      bufferHours: ITEM_BUFFER_HOURS[event.itemId],
      origin: 'history',
    });
  }
  rows.sort((a, b) => diffMinutes(a.start, b.start) || (a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0));
  return rows.map((row, index) => ({ ...row, id: `de-${String(index + 1).padStart(4, '0')}` }));
})();

/** Rows with `start >= from && end <= to`, in start order. */
export function downtimeEvents(
  from: SeriesTime = YTD_START,
  to: SeriesTime = BASIS_END,
): readonly DowntimeEvent[] {
  return FULL_LOG.filter(
    (row) => diffMinutes(row.start, from) >= 0 && diffMinutes(row.end, to) <= 0,
  );
}

/** The crew on shift when the event started — the ONLY place a crew is
 *  resolved, so every crew read downstream agrees on the same answer. */
export function eventCrewId(e: DowntimeEvent): CrewId {
  return activeCrewId(e.start);
}

export function eventsByKind(rows: readonly DowntimeEvent[]): {
  planned: DowntimeEvent[];
  unplanned: DowntimeEvent[];
} {
  const planned: DowntimeEvent[] = [];
  const unplanned: DowntimeEvent[] = [];
  for (const row of rows) {
    if (row.kind === 'planned') planned.push(row);
    else unplanned.push(row);
  }
  return { planned, unplanned };
}

export function sumHours(rows: readonly DowntimeEvent[]): number {
  return rows.reduce((total, row) => total + row.hours, 0);
}

export function causeCategoryLabel(id: CauseCategoryId): string {
  return CAUSE_CATEGORIES.find((row) => row.id === id)?.label ?? id;
}
